/**
 * TierVariationsPage — Side-by-side diff of two pricing tiers.
 *
 * Shows which modules / submodules / screens are:
 *   ● Only in Tier A  (red)
 *   ● Common to both  (green)
 *   ● Only in Tier B  (blue)
 *   ● Variations      (only_a + only_b combined)
 *
 * Data sources:
 *   GET /api/uams/tree
 *   GET /api/license/tier-privileges/:tier?industryCode=SHARED
 *   PUT /api/license/tier-privileges/:tier  ← Promote Changes
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GitCompare, Minus, Check, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTierList } from '../lib/useTierList';
import '../styles/erp-theme.css';
import { API, authFetch, apiFetch } from '../lib/api';


// Filter tabs — "Variations" = any diff (only_a | only_b)
const DIFF_FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'variations', label: 'Variations' },
  { id: 'both',       label: 'Common' },
  { id: 'only_a',     label: 'Only in A' },
  { id: 'only_b',     label: 'New in B' },
];
const FILTER_COLORS = { all: '#475569', variations: '#7c3aed', both: '#16a34a', only_a: '#dc2626', only_b: '#2563eb' };

/* ── StatusDot ────────────────────────────────────────────────────────────── */
function StatusDot({ enabled }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: enabled ? '#dcfce7' : '#f1f5f9', color: enabled ? '#16a34a' : '#94a3b8' }}>
      {enabled ? <Check size={12} strokeWidth={3} /> : <Minus size={12} />}
    </span>
  );
}

/* ── DiffPill ─────────────────────────────────────────────────────────────── */
const DIFF_STYLE = {
  only_a:     { bg: '#fef2f2', color: '#dc2626', label: '− Only A' },
  only_b:     { bg: '#eff6ff', color: '#2563eb', label: '+ Only B' },
  both:       { bg: '#f0fdf4', color: '#16a34a', label: '= Both'  },
  none:       { bg: '#f8fafc', color: '#94a3b8', label: '○ Neither' },
  variations: { bg: '#faf5ff', color: '#7c3aed', label: '≠ Varies' },
};
function DiffPill({ diff }) {
  const s = DIFF_STYLE[diff] || DIFF_STYLE.none;
  return <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

/* ── fetch privilege map + raw rows ──────────────────────────────────────── */
async function fetchPrivData(tier) {
  if (!tier) return { map: {}, rows: [] };
  const r = await apiFetch(`${API}/license/tier-privileges/${encodeURIComponent(tier)}?industryCode=SHARED`);
  if (!r.ok) return { map: {}, rows: [] };
  const rows = await r.json();
  const map = {};
  for (const row of (rows || [])) {
    map[`${row.EntityType}:${row.EntityId}`] = !!row.Enabled;
  }
  return { map, rows: rows || [] };
}

/* ── flatten tree ─────────────────────────────────────────────────────────── */
function flattenTree(tree, mapA, mapB) {
  const rows = [];
  for (const mod of tree) {
    const enA  = mapA[`Module:${mod.ModuleId}`] ?? false;
    const enB  = mapB[`Module:${mod.ModuleId}`] ?? false;
    const diff = enA && enB ? 'both' : enA ? 'only_a' : enB ? 'only_b' : 'none';
    const modLabel = mod.ModuleName || mod.label || `Module ${mod.ModuleId}`;
    rows.push({ type: 'module', id: mod.ModuleId, label: modLabel, modName: modLabel, subName: null, enA, enB, diff });

    for (const sub of (mod.subModules || [])) {
      const sA    = mapA[`SubModule:${sub.SubModuleId}`] ?? false;
      const sB    = mapB[`SubModule:${sub.SubModuleId}`] ?? false;
      const sDiff = sA && sB ? 'both' : sA ? 'only_a' : sB ? 'only_b' : 'none';
      const subLabel = sub.SubModuleName || sub.label || `Sub ${sub.SubModuleId}`;
      rows.push({ type: 'submodule', id: sub.SubModuleId, label: subLabel, modName: modLabel, subName: subLabel, enA: sA, enB: sB, diff: sDiff });

      for (const scr of (sub.screens || [])) {
        const cA    = mapA[`Screen:${scr.SeqId}`] ?? false;
        const cB    = mapB[`Screen:${scr.SeqId}`] ?? false;
        const cDiff = cA && cB ? 'both' : cA ? 'only_a' : cB ? 'only_b' : 'none';
        const scrLabel = (scr.ScreenTitle || scr.ScreenMenuName || scr.MenuTitle || scr.label || '').trim();
        rows.push({ type: 'screen', id: scr.SeqId, label: scrLabel, modName: modLabel, subName: subLabel, enA: cA, enB: cB, diff: cDiff });
      }
    }

    for (const scr of (mod.directScreens || [])) {
      const cA    = mapA[`Screen:${scr.SeqId}`] ?? false;
      const cB    = mapB[`Screen:${scr.SeqId}`] ?? false;
      const cDiff = cA && cB ? 'both' : cA ? 'only_a' : cB ? 'only_b' : 'none';
      const scrLabel = (scr.ScreenTitle || scr.ScreenMenuName || scr.MenuTitle || scr.label || '').trim();
      rows.push({ type: 'screen', id: scr.SeqId, label: scrLabel, modName: modLabel, subName: null, enA: cA, enB: cB, diff: cDiff });
    }
  }
  return rows;
}

/* ── PromoteButton (inline confirm) ──────────────────────────────────────── */
function PromoteButton({ row, targetTier, targetLabel, isTargetA, rawRows, onDone, onError }) {
  const [phase, setPhase] = useState('idle'); // idle | confirm | saving | done

  const ENTITY_MAP = { module: 'Module', submodule: 'SubModule', screen: 'Screen' };

  const doPromote = async () => {
    setPhase('saving');
    try {
      const entityType = ENTITY_MAP[row.type];
      const entityId   = String(row.id);
      // Build full updated list: enable only this entity, keep rest as-is
      const privileges = rawRows.map(r => ({
        entityType: r.EntityType,
        entityId:   String(r.EntityId),
        enabled: (r.EntityType === entityType && String(r.EntityId) === entityId) ? true : !!r.Enabled,
      }));
      const resp = await apiFetch(
        `${API}/license/tier-privileges/${encodeURIComponent(targetTier)}?industryCode=SHARED`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ privileges, industryCode: 'SHARED' }) }
      );
      const d = await resp.json();
      if (d.error) throw new Error(d.error);
      setPhase('done');
      setTimeout(() => { setPhase('idle'); onDone(); }, 1200);
    } catch (e) { setPhase('idle'); onError(e.message); }
  };

  if (phase === 'done')
    return <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#16a34a' }}>✓ Enabled</span>;

  if (phase === 'saving')
    return <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Saving…</span>;

  if (phase === 'confirm')
    return (
      <span style={{ display: 'inline-flex', gap: 4 }}>
        <button data-testid={`promote-confirm-${row.type}-${row.id}`}
          onClick={doPromote}
          style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 6, background: '#16a34a', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
          Confirm
        </button>
        <button onClick={() => setPhase('idle')}
          style={{ fontSize: '0.68rem', padding: '2px 6px', borderRadius: 6, background: '#f1f5f9', color: '#64748b', border: 'none', cursor: 'pointer' }}>
          ✕
        </button>
      </span>
    );

  const icon = isTargetA ? <ArrowLeft size={10} /> : <ArrowRight size={10} />;
  return (
    <button
      data-testid={`promote-btn-${row.type}-${row.id}`}
      onClick={() => setPhase('confirm')}
      title={`Enable ${row.label} for ${targetTier}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.69rem', fontWeight: 700, padding: '2px 8px',
        borderRadius: 8,
        background: isTargetA ? '#fef2f2' : '#eff6ff',
        color: isTargetA ? '#dc2626' : '#2563eb',
        border: `1px solid ${isTargetA ? '#fca5a5' : '#93c5fd'}`,
        cursor: 'pointer',
      }}
    >
      <Zap size={10} />{icon} {targetLabel}
    </button>
  );
}

/* ── Main Component ───────────────────────────────────────────────────────── */
export default function TierVariationsPage() {
  const { tiers, loading: tiersLoading } = useTierList();
  const [tierA, setTierA] = useState('');
  const [tierB, setTierB] = useState('');
  const [tree,  setTree]  = useState([]);
  const [mapA,  setMapA]  = useState({});
  const [mapB,  setMapB]  = useState({});
  const [rawA,  setRawA]  = useState([]);   // raw privilege rows for Tier A → needed by PromoteButton
  const [rawB,  setRawB]  = useState([]);   // raw privilege rows for Tier B
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffFilter,  setDiffFilter]  = useState('all');
  const [searchQ,     setSearchQ]     = useState('');
  const [promoteErr,  setPromoteErr]  = useState(null);

  useEffect(() => {
    if (tiers.length >= 2 && !tierA) { setTierA(tiers[0]); setTierB(tiers[1]); }
  }, [tiers, tierA]);

  useEffect(() => {
    setLoadingTree(true);
    apiFetch(`${API}/uams/tree`)
      .then(r => r.json())
      .then(d => setTree(d.tree || []))
      .catch(() => setTree([]))
      .finally(() => setLoadingTree(false));
  }, []);

  const reloadMaps = useCallback(() => {
    if (!tierA && !tierB) return;
    setLoadingDiff(true);
    Promise.all([fetchPrivData(tierA), fetchPrivData(tierB)])
      .then(([a, b]) => { setMapA(a.map); setRawA(a.rows); setMapB(b.map); setRawB(b.rows); })
      .finally(() => setLoadingDiff(false));
  }, [tierA, tierB]);

  useEffect(() => { reloadMaps(); }, [reloadMaps]);

  const allRows = useMemo(() => flattenTree(tree, mapA, mapB), [tree, mapA, mapB]);

  const rows = useMemo(() => {
    let r = allRows;
    if (diffFilter === 'variations') r = r.filter(x => x.diff === 'only_a' || x.diff === 'only_b');
    else if (diffFilter !== 'all')   r = r.filter(x => x.diff === diffFilter);
    if (searchQ.trim()) { const q = searchQ.toLowerCase(); r = r.filter(x => String(x.label).toLowerCase().includes(q)); }
    return r;
  }, [allRows, diffFilter, searchQ]);

  // Counts for filter badges
  const counts = useMemo(() => ({
    all:        allRows.length,
    variations: allRows.filter(r => r.diff === 'only_a' || r.diff === 'only_b').length,
    only_a:     allRows.filter(r => r.diff === 'only_a').length,
    only_b:     allRows.filter(r => r.diff === 'only_b').length,
    both:       allRows.filter(r => r.diff === 'both').length,
    none:       allRows.filter(r => r.diff === 'none').length,
  }), [allRows]);

  // Per-type breakdown for analytics cards (includes A/B enabled totals)
  const typedCounts = useMemo(() => {
    const init = () => ({ only_a: 0, both: 0, only_b: 0, none: 0, total: 0, enabledA: 0, enabledB: 0 });
    const result = { modules: init(), submodules: init(), screens: init() };
    for (const row of allRows) {
      const key = row.type === 'module' ? 'modules' : row.type === 'submodule' ? 'submodules' : 'screens';
      result[key][row.diff]++;
      result[key].total++;
      if (row.enA) result[key].enabledA++;
      if (row.enB) result[key].enabledB++;
    }
    return result;
  }, [allRows]);

  const loading = loadingTree || loadingDiff || tiersLoading;

  const TYPE_STYLE = {
    module:    { indent: 0,  fg: '#0f172a', fw: 700, fs: '0.86rem' },
    submodule: { indent: 20, fg: '#334155', fw: 600, fs: '0.82rem' },
    screen:    { indent: 40, fg: '#64748b', fw: 400, fs: '0.79rem' },
  };
  const DIFF_ROW_STYLE = {
    only_a: { bg: '#fff5f5', border: '3px solid #fca5a5' },
    only_b: { bg: '#eff6ff', border: '3px solid #93c5fd' },
    both:   { bg: '#f0fdf4', border: '3px solid #86efac' },
    none:   { bg: '#fff',    border: '3px solid transparent' },
  };

  const CARDS = [
    { key: 'modules',    label: 'Modules',   iconColor: '#0f172a' },
    { key: 'submodules', label: 'SubModules', iconColor: '#334155' },
    { key: 'screens',    label: 'Screens',    iconColor: '#64748b' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'inherit' }}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(180deg,#fff 0%,#f8fafc 100%)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
          <GitCompare size={18} color="#3b82f6" />
          <span style={{ fontWeight: 700, fontSize: '0.93rem', color: '#0f172a' }}>Tier Variations</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 auto', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.79rem', color: '#64748b', fontWeight: 600 }}>Tier A</label>
          <select value={tierA} onChange={e => setTierA(e.target.value)} data-testid="tier-a-select"
            style={{ padding: '4px 8px', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fef2f2', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>
            <option value="">-- Select --</option>
            {tiers.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600 }}>vs</span>
          <label style={{ fontSize: '0.79rem', color: '#64748b', fontWeight: 600 }}>Tier B</label>
          <select value={tierB} onChange={e => setTierB(e.target.value)} data-testid="tier-b-select"
            style={{ padding: '4px 8px', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 6, background: '#eff6ff', color: '#2563eb', fontWeight: 700, cursor: 'pointer' }}>
            <option value="">-- Select --</option>
            {tiers.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="text" placeholder="Search entity…" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            data-testid="tier-diff-search"
            style={{ padding: '4px 10px', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 160 }} />
        </div>
      </div>

      {/* ── Analytics Cards (A vs B counts) ──────────────────────────── */}
      {tierA && tierB && !loading && (
        <div style={{ display: 'flex', gap: 10, padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          {CARDS.map(card => {
            const tc = typedCounts[card.key];
            const pctA = tc.total ? Math.round((tc.enabledA / tc.total) * 100) : 0;
            const pctB = tc.total ? Math.round((tc.enabledB / tc.total) * 100) : 0;
            return (
              <div key={card.key} data-testid={`analytics-card-${card.key}`}
                style={{ flex: '1 1 200px', minWidth: 180, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                {/* Card title + total */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: '0.74rem', fontWeight: 700, color: card.iconColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{card.label}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8' }}>{tc.total} total</span>
                </div>
                {/* A vs B enabled counts */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 5 }}>
                  <div style={{ background: '#fef2f2', borderRadius: 6, padding: '4px 8px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', marginBottom: 1 }}>{tierA}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#991b1b', lineHeight: 1 }}>{tc.enabledA}</div>
                    <div style={{ fontSize: '0.65rem', color: '#fca5a5', marginTop: 1 }}>{pctA}% enabled</div>
                  </div>
                  <div style={{ background: '#eff6ff', borderRadius: 6, padding: '4px 8px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', marginBottom: 1 }}>{tierB}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#1e40af', lineHeight: 1 }}>{tc.enabledB}</div>
                    <div style={{ fontSize: '0.65rem', color: '#93c5fd', marginTop: 1 }}>{pctB}% enabled</div>
                  </div>
                </div>
                {/* Diff breakdown badges */}
                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {tc.only_a > 0 && <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#fef2f2', color: '#dc2626' }}>−A:{tc.only_a}</span>}
                  {tc.both   > 0 && <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#f0fdf4', color: '#16a34a' }}>={tc.both}</span>}
                  {tc.only_b > 0 && <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#eff6ff', color: '#2563eb' }}>+B:{tc.only_b}</span>}
                  {tc.none   > 0 && <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '1px 5px', borderRadius: 8, background: '#f1f5f9', color: '#94a3b8' }}>○{tc.none}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Sticky filter bar ─────────────────────────────────────────── */}
      {tierA && tierB && !loading && (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', gap: 6, padding: '7px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexWrap: 'wrap', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          {DIFF_FILTERS.map(f => {
            const cnt = counts[f.id] ?? 0;
            const active = diffFilter === f.id;
            const color  = FILTER_COLORS[f.id] || '#475569';
            return (
              <button key={f.id} data-testid={`diff-filter-${f.id}`} onClick={() => setDiffFilter(f.id)}
                style={{ padding: '4px 11px', fontSize: '0.79rem', fontWeight: 700, border: `2px solid ${active ? color : '#e2e8f0'}`, borderRadius: 20, background: active ? `${color}12` : '#fff', color: active ? color : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                {f.label}
                <span style={{ background: active ? color : '#e2e8f0', color: active ? '#fff' : '#64748b', borderRadius: 10, fontSize: '0.69rem', padding: '1px 5px', fontWeight: 700 }}>{cnt}</span>
              </button>
            );
          })}
          {promoteErr && (
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#dc2626', alignSelf: 'center' }}>
              Promote failed: {promoteErr} <button onClick={() => setPromoteErr(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.75rem' }}>✕</button>
            </span>
          )}
          <span style={{ marginLeft: promoteErr ? 0 : 'auto', fontSize: '0.79rem', color: '#94a3b8', alignSelf: 'center' }}>
            {rows.length} rows
          </span>
        </div>
      )}

      {/* ── Loading / empty states ────────────────────────────────────── */}
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.86rem' }}>
          Loading comparison data…
        </div>
      )}
      {!loading && (!tierA || !tierB) && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#94a3b8' }}>
          <GitCompare size={40} strokeWidth={1} />
          <p style={{ fontSize: '0.86rem', fontWeight: 600 }}>Select two tiers to compare</p>
          <p style={{ fontSize: '0.79rem' }}>Differences will be highlighted across all modules, submodules and screens.</p>
        </div>
      )}

      {/* ── Diff table ────────────────────────────────────────────────── */}
      {!loading && tierA && tierB && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <th style={{ background: '#1e293b', color: '#fff', padding: '7px 12px', textAlign: 'left', fontWeight: 700, fontSize: '0.79rem', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                  Module / Submodule / Screen
                </th>
                <th style={{ background: '#b91c1c', color: '#fff', padding: '7px 16px', textAlign: 'center', fontWeight: 700, fontSize: '0.79rem', borderRight: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap', minWidth: 100 }}>
                  {tierA}
                </th>
                <th style={{ background: '#1d4ed8', color: '#fff', padding: '7px 16px', textAlign: 'center', fontWeight: 700, fontSize: '0.79rem', borderRight: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap', minWidth: 100 }}>
                  {tierB}
                </th>
                <th style={{ background: '#1e293b', color: '#94a3b8', padding: '7px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.79rem', minWidth: 80 }}>
                  Diff
                </th>
                <th style={{ background: '#1e293b', color: '#94a3b8', padding: '7px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.79rem', minWidth: 120 }}>
                  Promote
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '0.86rem' }}>No entities match the current filter.</td></tr>
              )}
              {rows.map((row, i) => {
                const ts = TYPE_STYLE[row.type] || TYPE_STYLE.screen;
                const rs = DIFF_ROW_STYLE[row.diff] || DIFF_ROW_STYLE.none;
                const rowBg = row.diff === 'none' ? (i % 2 === 0 ? '#fff' : '#f8fafc') : rs.bg;
                return (
                  <tr key={`${row.type}-${row.id}-${i}`} style={{ background: rowBg }}>
                    <td style={{ padding: `5px 12px`, borderBottom: '1px solid #f1f5f9', borderLeft: rs.border }}>
                      {row.type === 'module' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff', background: '#1e293b', padding: '2px 7px', borderRadius: 3, letterSpacing: '0.05em' }}>MOD</span>
                          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{row.label}</span>
                        </span>
                      )}
                      {row.type === 'submodule' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 18 }}>
                          <span style={{ color: '#1e40af', fontSize: '0.84rem', fontWeight: 600 }}>{row.modName}</span>
                          <span style={{ color: '#475569', fontSize: '0.84rem', fontWeight: 700 }}>›</span>
                          <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff', background: '#2563eb', padding: '2px 7px', borderRadius: 3, letterSpacing: '0.05em' }}>SUB</span>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e3a8a' }}>{row.label}</span>
                        </span>
                      )}
                      {row.type === 'screen' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 36 }}>
                          <span style={{ color: '#374151', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.modName}</span>
                          <span style={{ color: '#374151', fontSize: '0.82rem', fontWeight: 700 }}>›</span>
                          {row.subName && <><span style={{ color: '#374151', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.subName}</span><span style={{ color: '#374151', fontSize: '0.82rem', fontWeight: 700 }}>›</span></>}
                          <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#fff', background: '#059669', padding: '2px 7px', borderRadius: 3, letterSpacing: '0.05em' }}>SCR</span>
                          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#064e3b' }}>{row.label}</span>
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}><StatusDot enabled={row.enA} /></td>
                    <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}><StatusDot enabled={row.enB} /></td>
                    <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: '1px solid #f1f5f9' }}><DiffPill diff={row.diff} /></td>
                    <td style={{ textAlign: 'center', padding: '4px 8px', borderBottom: '1px solid #f1f5f9' }}>
                      {row.diff === 'only_a' && (
                        <PromoteButton row={row} targetTier={tierB} targetLabel={`→ ${tierB}`}
                          isTargetA={false} rawRows={rawB}
                          onDone={reloadMaps}
                          onError={msg => setPromoteErr(msg)} />
                      )}
                      {row.diff === 'only_b' && (
                        <PromoteButton row={row} targetTier={tierA} targetLabel={`${tierA} ←`}
                          isTargetA={true} rawRows={rawA}
                          onDone={reloadMaps}
                          onError={msg => setPromoteErr(msg)} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
